// ============================================================================
// The command center's two reads (item 701).
//
//   readBridge(view) — the board, through the build-room-bridge function. The
//                      key lives there; nothing here ever sees it.
//   readOpenFeedback — open app_feedback rows, read directly through the app's
//                      own client (a superuser already has that access).
//
// ⛔ A FAILED READ IS NEVER AN EMPTY LIST (PLAYBOOK §15 q.3). Each returns
// either data or a sentence saying, in plain words, why there is none.
// ============================================================================
import { getSupabaseToken, supabase } from '../../shared/supabase';
import { errorMessage } from '../../shared/errors';
import type {
  BoardItem,
  DemosView,
  DemoView,
  FeedbackLinksView,
  ItemsView,
  RunsView,
} from '../../../supabase/functions/build-room-bridge/handler';
import { CLOSED_FEEDBACK } from './derive';
import { bridgeErrorWords } from './bridge-words';

export type { DemosView, DemoView, FeedbackLinksView, ItemsView, RunsView };

export type Read<T> = { ok: true; data: T } | { ok: false; message: string };

const BRIDGE_URL = `${import.meta.env.VITE_SUPABASE_URL as string}/functions/v1/build-room-bridge`;

type ViewData = { items: ItemsView; runs: RunsView; demos: DemosView };

export async function readBridge<V extends 'items' | 'runs' | 'demos'>(view: V, query = ''): Promise<Read<ViewData[V]>> {
  const token = getSupabaseToken();
  if (!token) return { ok: false, message: 'You are not signed in, so the board cannot be read.' };
  let res: Response;
  try {
    res = await fetch(`${BRIDGE_URL}?view=${view}${query}`, { headers: { Authorization: `Bearer ${token}` } });
  } catch (e) {
    return { ok: false, message: `The bridge could not be reached. ${errorMessage(e, '')}`.trim() };
  }
  let body: unknown = null;
  try {
    body = await res.json();
  } catch {
    body = null;
  }
  if (!res.ok) return { ok: false, message: bridgeErrorWords(res.status, body as { error?: string } | null, view) };
  return { ok: true, data: body as ViewData[V] };
}

/** One demo from the shelf (item #827). Read only when "See the demo" is pressed. */
export async function readDemo(item: number): Promise<Read<DemoView>> {
  const token = getSupabaseToken();
  if (!token) return { ok: false, message: 'You are not signed in, so the demo cannot be read.' };
  try {
    const res = await fetch(`${BRIDGE_URL}?view=demo&item=${item}`, { headers: { Authorization: `Bearer ${token}` } });
    const body = (await res.json().catch(() => null)) as { error?: string; message?: string } | DemoView | null;
    if (!res.ok) {
      const b = body as { error?: string; message?: string } | null;
      return { ok: false, message: b?.message ?? bridgeErrorWords(res.status, b, 'demo') };
    }
    return { ok: true, data: body as DemoView };
  } catch (e) {
    return { ok: false, message: `The bridge could not be reached. ${errorMessage(e, '')}`.trim() };
  }
}

export interface FeedbackRow {
  feedback_id: string;
  title: string;
  description: string;
  status: string;
  created_at: string;
  who: string;
}

export async function readOpenFeedback(): Promise<Read<FeedbackRow[]>> {
  const { data, error } = await supabase
    .from('app_feedback')
    .select('feedback_id, title, description, status, created_at, submitted_by_employee_id, submitted_by_display_name')
    .not('status', 'in', `(${CLOSED_FEEDBACK.join(',')})`)
    .order('created_at', { ascending: false });
  if (error) return { ok: false, message: `Feedback could not be read. ${errorMessage(error, '')}`.trim() };
  const rows = (data ?? []) as Array<{
    feedback_id: string;
    title: string | null;
    description: string | null;
    status: string;
    created_at: string;
    submitted_by_employee_id: string | null;
    submitted_by_display_name: string | null;
  }>;

  // The name comes from v_employee_display, the house resolver (admin/feedback.tsx);
  // the stored display name is the fallback. A failed lookup costs a name, not the list.
  const eids = [...new Set(rows.map((r) => r.submitted_by_employee_id).filter(Boolean))] as string[];
  const names = new Map<string, string>();
  if (eids.length) {
    const { data: named, error: nameErr } = await supabase
      .from('v_employee_display')
      .select('employee_id, employee_full_name')
      .in('employee_id', eids);
    if (nameErr) console.warn('[build-room] submitter lookup failed', nameErr);
    for (const n of (named ?? []) as Array<{ employee_id: string; employee_full_name: string | null }>) {
      if (n.employee_full_name) names.set(n.employee_id, n.employee_full_name);
    }
  }

  return {
    ok: true,
    data: rows.map((r) => ({
      feedback_id: r.feedback_id,
      title: r.title ?? '(no title)',
      description: r.description ?? '',
      status: r.status,
      created_at: r.created_at,
      who:
        (r.submitted_by_employee_id && names.get(r.submitted_by_employee_id)) ||
        r.submitted_by_display_name ||
        'someone not recorded',
    })),
  };
}

// ----- Run 2 (item #702): who may act, the writes, and the feedback links -----------

export interface Whoami {
  canAct: boolean;
  reason?: string;
  login?: string;
}

async function callBridge<T>(init: RequestInit, query: string, view: 'items' | 'runs' = 'items'): Promise<Read<T>> {
  const token = getSupabaseToken();
  if (!token) return { ok: false, message: 'You are not signed in, so the board cannot be reached.' };
  let res: Response;
  try {
    res = await fetch(`${BRIDGE_URL}${query}`, {
      ...init,
      headers: { Authorization: `Bearer ${token}`, ...(init.body ? { 'Content-Type': 'application/json' } : {}) },
    });
  } catch (e) {
    return { ok: false, message: `The bridge could not be reached. ${errorMessage(e, '')}`.trim() };
  }
  let body: unknown = null;
  try {
    body = await res.json();
  } catch {
    body = null;
  }
  if (!res.ok) {
    // A write's refusal arrives as a sentence of its own; say exactly that.
    const said = (body as { message?: string } | null)?.message;
    return { ok: false, message: said || bridgeErrorWords(res.status, body as { error?: string } | null, view) };
  }
  return { ok: true, data: body as T };
}

/** May the person signed in press the buttons? Read before any button is offered. */
export const readWhoami = () => callBridge<Whoami>({}, '?view=whoami');

/** For each open feedback id (at most 50 a call), the closed items that name it. */
export async function readFeedbackLinks(ids: string[]): Promise<Read<FeedbackLinksView>> {
  if (ids.length === 0) return { ok: true, data: { links: {}, read_at: new Date().toISOString() } };
  const merged: FeedbackLinksView = { links: {}, read_at: '' };
  for (let i = 0; i < ids.length; i += 50) {
    const r = await callBridge<FeedbackLinksView>({}, `?view=feedback-links&ids=${encodeURIComponent(ids.slice(i, i + 50).join(','))}`);
    if (!r.ok) return r;
    Object.assign(merged.links, r.data.links);
    merged.read_at = r.data.read_at;
  }
  return { ok: true, data: merged };
}

export type WriteAction = 'approve' | 'pushback' | 'park' | 'unpark' | 'priority' | 'pin' | 'unpin' | 'merge' | 'close' | 'close-settled' | 'keep-open' | 'send-back' | 'mark-building';

export interface WriteResult {
  ok: true;
  action: WriteAction;
  wrote: string;
  at: string;
  signed_by: string;
  item: BoardItem | null;
  item_state: 'open' | 'closed' | null;
}

/** One press, one call. The answer is what GitHub now has, or the bridge's sentence. */
export const writeBridge = (body: { action: WriteAction; item: number } & Record<string, unknown>) =>
  callBridge<WriteResult>({ method: 'POST', body: JSON.stringify(body) }, '');
